import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const translations = {
  en: {
    // Nav
    markets: 'Markets',
    platform: 'Platform',
    accounts: 'Accounts',
    knowledgeHub: 'Knowledge Hub',
    faq: 'FAQ',
    login: 'Login',
    openAccount: 'Open Account',
    // Hero
    heroTagline: 'Institutional Trading Platform',
    heroTitle1: 'Trade Global',
    heroTitle2: 'Markets with',
    heroTitle3: 'Confidence',
    heroDescription: 'Professional trading infrastructure with institutional-grade execution, real-time market data, and advanced analytics.',
    whoWeAre: 'Who We Are',
    whoWeAreText: "We are one of the world's largest privately-held investment management firms, respected and admired for the quality of our independent research, the calibre of our people, and our ability to build lasting relationships with our clients. Since 1928 we have continued to innovate while remaining faithful to what most distinguishes us: our private ownership structure, which safeguards the independence of investment decisions on the one hand, and our collaborative culture, which encourages healthy intellectual debate on the other. We believe these are the defining elements of the way we choose to conduct our business — always placing our clients at the centre of every decision we make.",
    openTradingAccount: 'Open a Trading Account',
    exploreWebTrader: 'Explore WebTrader',
    // Sections
    marketCategoriesTitle: 'Global Markets',
    marketCategoriesSubtitle: 'Trade the most popular instruments across all asset classes',
    liveMarkets: 'Live Markets',
    economicCalendar: 'Economic Calendar',
    economicCalendarSubtitle: 'Track key economic events and market-moving data releases',
    whyTitle: 'Built for Serious Traders',
    tradeEverything: 'Trade Everything, One Platform',
    professionalTerminal: 'Professional Trading Terminal',
    startTradingToday: 'Start Trading Today',
    demoBalanceCta: 'Open your demo account in minutes with $10,000 virtual funds',
    createFreeAccount: 'Create Free Account',
    // Categories
    popular: 'Popular',
    forex: 'Forex',
    cryptoCFDs: 'Crypto CFDs',
    metals: 'Metals',
    indices: 'Indices',
    futures: 'Futures',
    energy: 'Energy',
    shares: 'Shares',
    etfs: 'ETFs',
    // Common
    trade: 'Trade',
    bid: 'Bid',
    ask: 'Ask',
    change: 'Change',
    spread: 'Spread',
    demoMode: '⚠ DEMO/SANDBOX MODE: All trading is simulated. Market prices may not reflect real-time values.',
    // Calendar
    dateCol: 'Date',
    timeCol: 'Time',
    currencyCol: 'Currency',
    countryCol: 'Country',
    eventCol: 'Event',
    importanceCol: 'Importance',
    previousCol: 'Previous',
    forecastCol: 'Forecast',
    actualCol: 'Actual',
    filterToday: 'Today',
    filterTomorrow: 'Tomorrow',
    filterWeek: 'This Week',
    filterAll: 'All Events',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    // Knowledge Hub
    khTitle: 'Knowledge Hub',
    khSubtitle: 'Learn to trade with expert guides, tutorials, and analysis',
    khViewAll: 'View All',
    basicsOfTrading: 'Basics of Trading',
    fundamentalAnalysis: 'Fundamental Analysis',
    technicalAnalysis: 'Technical Analysis',
    tradersGlossary: 'Traders Glossary',
    traderPsychology: 'Trader Psychology',
    cmeEducation: 'CME Group Education',
    articles: 'Articles',
    openDemoAccount: 'Open Demo Account',
    videoWebinars: 'Video & Webinars',
    fxproWebinars: 'FxPro Webinars',
    watchAndLearn: 'Watch and Learn',
    latestArticles: 'Latest Articles',
    featuredArticles: 'Featured Articles',
    readMore: 'Read More',
    searchArticles: 'Search articles...',
    // Footer
    products: 'Products',
    company: 'Company',
    legal: 'Legal',
    rights: '© Copyright 2026 W M I Ltd. Tutti i diritti riservati.\nW M FUNDS ® è un marchio di servizio registrato di W G Holdings LLP.\nW Europe GmbH. Sede legale: Niederlassung Deutschland Bockenheimer Landstraße 43-47, 60325 Francoforte sul Meno',
    termsAndConditionsTitle: 'Terms & Conditions',
    investmentRisks: 'Investment risks',
    termsOfUse: 'Terms of use',
    dataProtectionNotice: 'Data protection notice',
    cookiePolicy: 'Cookie policy',
    proxyVoting: 'Proxy voting',
    followUsSocial: 'Follow us on social media',
    contactsTitle: 'Contacts',
    contactTeam: 'Contact the team',
    officesWorldwide: 'Our offices worldwide',
    contactUs: 'Contact us',
  },
  it: {
    // Nav
    markets: 'Mercati',
    platform: 'Piattaforma',
    accounts: 'Conti',
    knowledgeHub: 'Centro Conoscenza',
    faq: 'FAQ',
    login: 'Accedi',
    openAccount: 'Apri Conto',
    // Hero
    heroTagline: 'Piattaforma Trading Istituzionale',
    heroTitle1: 'Fai Trading',
    heroTitle2: 'sui Mercati Globali',
    heroTitle3: 'con Sicurezza',
    heroDescription: 'Infrastruttura di trading professionale con esecuzione istituzionale, dati di mercato in tempo reale e analisi avanzate.',
    whoWeAre: 'Chi Siamo',
    whoWeAreText: "Siamo una delle più grandi società di gestione a capitale privato al mondo, apprezzati e stimati per la qualità della nostra ricerca indipendente, la professionalità dei nostri collaboratori e la capacità di sviluppare relazioni durature con i nostri clienti. Dal 1928 continuiamo ad innovare pur restando fedeli a ciò che più ci contraddistingue: la nostra struttura privata che salvaguarda l'indipendenza delle decisioni di investimento da un lato e la natura collaborativa che promuove un sano dibattito intellettuale dall'altro. Crediamo che siano questi gli elementi distintivi del modo in cui abbiamo scelto di condurre le nostre attività mettendo sempre i nostri clienti al centro di ogni nostra decisione.",
    openTradingAccount: 'Apri un Conto Trading',
    exploreWebTrader: 'Esplora WebTrader',
    // Sections
    marketCategoriesTitle: 'Mercati Globali',
    marketCategoriesSubtitle: 'Fai trading sugli strumenti più popolari in tutte le asset class',
    liveMarkets: 'Mercati in Diretta',
    economicCalendar: 'Calendario Economico',
    economicCalendarSubtitle: 'Segui eventi economici chiave e rilasci di dati che muovono i mercati',
    whyTitle: 'Costruito per Trader Seri',
    tradeEverything: 'Fai Trading di Tutto, Una Piattaforma',
    professionalTerminal: 'Terminale di Trading Professionale',
    startTradingToday: 'Inizia a Fare Trading Oggi',
    demoBalanceCta: 'Apri il tuo conto demo in pochi minuti con $10.000 di fondi virtuali',
    createFreeAccount: 'Crea Conto Gratuito',
    // Categories
    popular: 'Popolari',
    forex: 'Forex',
    cryptoCFDs: 'CFD Crypto',
    metals: 'Metalli',
    indices: 'Indici',
    futures: 'Futures',
    energy: 'Energia',
    shares: 'Azioni',
    etfs: 'ETF',
    // Common
    trade: 'Trada',
    bid: 'Denaro',
    ask: 'Lettera',
    change: 'Variazione',
    spread: 'Spread',
    demoMode: '⚠ MODALITÀ DEMO/SANDBOX: Tutto il trading è simulato. I prezzi di mercato potrebbero non riflettere valori in tempo reale.',
    // Calendar
    dateCol: 'Data',
    timeCol: 'Ora',
    currencyCol: 'Valuta',
    countryCol: 'Paese',
    eventCol: 'Evento',
    importanceCol: 'Importanza',
    previousCol: 'Precedente',
    forecastCol: 'Previsione',
    actualCol: 'Effettivo',
    filterToday: 'Oggi',
    filterTomorrow: 'Domani',
    filterWeek: 'Questa Settimana',
    filterAll: 'Tutti gli Eventi',
    high: 'Alta',
    medium: 'Media',
    low: 'Bassa',
    // Knowledge Hub
    khTitle: 'Centro Conoscenza',
    khSubtitle: 'Impara a fare trading con guide, tutorial e analisi di esperti',
    khViewAll: 'Vedi Tutto',
    basicsOfTrading: 'Basi del Trading',
    fundamentalAnalysis: 'Analisi Fondamentale',
    technicalAnalysis: 'Analisi Tecnica',
    tradersGlossary: 'Glossario del Trader',
    traderPsychology: 'Psicologia del Trader',
    cmeEducation: 'CME Group Education',
    articles: 'Articoli',
    openDemoAccount: 'Apri Conto Demo',
    videoWebinars: 'Video e Webinar',
    fxproWebinars: 'Webinar FxPro',
    watchAndLearn: 'Guarda e Impara',
    latestArticles: 'Ultimi Articoli',
    featuredArticles: 'Articoli in Evidenza',
    readMore: 'Leggi di più',
    searchArticles: 'Cerca articoli...',
    // Footer
    products: 'Prodotti',
    company: 'Azienda',
    legal: 'Legale',
    rights: '© Copyright 2026 W M I Ltd. Tutti i diritti riservati.\nW M FUNDS ® è un marchio di servizio registrato di W G Holdings LLP.\nW Europe GmbH. Sede legale: Niederlassung Deutschland Bockenheimer Landstraße 43-47, 60325 Francoforte sul Meno',
    termsAndConditionsTitle: 'Termini e Condizioni',
    investmentRisks: 'Rischi legati all\u2019investimento',
    termsOfUse: 'Termini di utilizzo',
    dataProtectionNotice: 'Informativa sulla protezione dei dati',
    cookiePolicy: 'Informativa sui cookie',
    proxyVoting: 'Voto per delega',
    followUsSocial: 'Seguici sui social media',
    contactsTitle: 'Contatti',
    contactTeam: 'Contatta il team',
    officesWorldwide: 'I nostri uffici nel mondo',
    contactUs: 'Contattaci',
  }
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => localStorage.getItem('wcdfs_lang') || 'en');

  useEffect(() => {
    localStorage.setItem('wcdfs_lang', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => translations[language]?.[key] || translations.en[key] || key;

  const contextValue = useMemo(
    () => ({ language, setLanguage, t }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
